import React from 'react';

import { useIntl } from 'react-intl-next';

import Badge from '@atlaskit/badge';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import FilesIcon from '@atlaskit/icon/core/files';

import { universalTypeFilterId } from '../../../../../common/constants/filters';
import type { SelectFilterOption } from '../../../../../common/constants/filters/select-filter/types';
import { type UniversalTypeFilterMapping } from '../../../../../common/constants/filters/types';
import { allTypeFilterValueKeys } from '../../../../../common/constants/filters/universal-type';
import {
	type ProductKeys,
	ProductKeys as ProductKeysEnum,
} from '../../../../../common/constants/products';
import { FilterProductIcon } from '../../../../../common/ui/filters/filter-product-icon';
import { FilterButton } from '../../../../../common/ui/quick-find/filter-button';
import { FilterButtonIconContainer } from '../../../../../common/ui/quick-find/filter-button/filter-button-icon-container';
import { FilterPopupSelect } from '../../../../../common/ui/quick-find/filter-popup-select';
import { getTypeMapping } from '../../../../../common/utils/filters/constants';
import { useUniversalSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import {
	useFiltersDiscoverability2Enabled,
	usePrimaryProduct,
} from '../../../../../controllers/store/bootstrap';
import { useSelectedProduct } from '../../../../../controllers/store/search';

import { i18n } from './messages';

const getOptionsToProducts = (typeMapping: UniversalTypeFilterMapping) => {
	const optionsToProducts: Record<string, ProductKeys[]> = {};
	allTypeFilterValueKeys.forEach((type) => {
		optionsToProducts[type] = typeMapping[type].products || [];
	});
	return optionsToProducts;
};

export const UniversalTypeFilter = ({
	iconBefore,
	iconAfter,
	showPrimaryProductIcon,
}: {
	iconBefore?: React.ReactNode;
	iconAfter?: React.ReactNode;
	showPrimaryProductIcon?: boolean;
}): React.JSX.Element | null => {
	const [primaryProduct] = usePrimaryProduct();
	const { formatMessage } = useIntl();
	const typeMapping = getTypeMapping();
	const optionsToProducts = getOptionsToProducts(typeMapping);
	const [productSelected] = useSelectedProduct();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();

	const [
		filterData,
		{ setOption, loadInitialOptions, setLookupInput, clearLookupInput, clearOptions },
	] = useUniversalSelectFilter({
		filterId: universalTypeFilterId,
		optionsToProducts,
	});

	if (!filterData) {
		return null;
	}

	// Do not show TypeFilter for Atlas since that is handled by Noun filters
	if (productSelected === ProductKeysEnum.Atlas) {
		return null;
	}

	const { options, lookupInput, selectedOptions, isLoading, isLookupDefined } = filterData;

	// Compute the label and badge values since useUniversalSelectFilter doesn't provide them
	const selectedOptionsCount = selectedOptions.length;
	const badgeLabel = selectedOptionsCount > 1 ? `+${selectedOptionsCount - 1}` : undefined;
	const firstSelectedOptionLabel = selectedOptions.at(0)?.label;

	const getIconBefore = () => {
		if (iconBefore) {
			return iconBefore;
		}

		return (
			<FilterButtonIconContainer>
				{showPrimaryProductIcon && (
					<FilterProductIcon
						showPrimaryProductIcon={showPrimaryProductIcon}
						primaryProduct={primaryProduct}
					/>
				)}
				<FilesIcon label={formatMessage(i18n.type)} />
			</FilterButtonIconContainer>
		);
	};

	const getIconAfter = () => {
		return selectedOptions.length === 0 ? (
			iconAfter ? (
				iconAfter
			) : (
				<ChevronDownIcon label="" size="small" />
			)
		) : (
			badgeLabel && <Badge appearance="information">{badgeLabel}</Badge>
		);
	};

	const handleSetOption = (option: SelectFilterOption | SelectFilterOption[]) => {
		setOption(option);
	};

	return (
		<FilterPopupSelect
			filterId={universalTypeFilterId}
			canClearFilter
			checkboxIndicators={false}
			onDropdownOpen={loadInitialOptions}
			onDropdownClose={clearLookupInput}
			onSearchInputChange={setLookupInput}
			onSelectChange={handleSetOption}
			options={options}
			selectedOptions={selectedOptions}
			label={formatMessage(i18n.type)}
			placeholder={formatMessage(i18n.filterTypePlaceholder)}
			isLoading={isLoading}
			isSearchable={isLookupDefined}
			searchInput={lookupInput}
			onClearOptions={clearOptions}
			target={({ isOpen, ...targetProps }) => (
				<FilterButton
					testId="quick-find-universal-type-filter-button"
					{...targetProps}
					isSelected={!!selectedOptions.length || isOpen}
					iconBefore={getIconBefore()}
					iconAfter={getIconAfter()}
					minWidthZero={true}
				>
					{firstSelectedOptionLabel
						? `${formatMessage(i18n.type)}: ${firstSelectedOptionLabel}`
						: formatMessage(i18n.type)}
				</FilterButton>
			)}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
		/>
	);
};
